import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { ROLE_TOOLS, sameTools, TOOL_CALLS_DEFAULT, TOOL_CALLS_MAX, TOOL_DEFS, TOOL_KEYS, type AgentRole, type AgentTemplateDTO, type Permission } from '@harness/shared';
import { Modal, PageHeader, Seg, Spinner, Switch, SwitchRow } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { useModels } from '../../lib/queries';
import { toast } from '../../lib/store';

// Admin › Agent library: agents everyone can add to their projects out of the box. Changes apply to
// projects created (or agents added) afterwards; projects keep their own copy and can tune it.

const key = ['admin', 'agent-templates'] as const;
const ROLES: AgentRole[] = ['Planner', 'Coder', 'Reviewer', 'Researcher', 'Wireframer', 'Analyst', 'Custom'];
const PERMS: { value: Permission; label: string }[] = [
  { value: 'allow', label: 'Always allow' },
  { value: 'ask', label: 'Ask' },
  { value: 'deny', label: 'Deny' },
];

type Draft = Omit<AgentTemplateDTO, 'id' | 'updatedAt'> & { id?: string };

const blank = (): Draft => ({
  name: '',
  description: '',
  role: 'Custom',
  systemPrompt: 'You are an agent for {{project.name}}. Work inside {{server.cwd}}.\nToday is {{date}}.',
  preferredModel: null,
  params: {},
  tools: ROLE_TOOLS.Custom,
  contextFiles: ['AGENTS.md'],
  limits: { maxToolCalls: TOOL_CALLS_DEFAULT, maxSpendUsd: null },
  includeByDefault: false,
  isDefaultAgent: false,
  enabled: true,
  sort: 100,
});

export default function LibraryPage() {
  const lib = useQuery({ queryKey: key, queryFn: () => api.get<AgentTemplateDTO[]>('/api/admin/agent-templates') });
  const [edit, setEdit] = useState<Draft | null>(null);
  return (
    <div className="settings-content">
      <PageHeader title="Agent library" sub="Agents people can add to their projects out of the box. New projects start with the ones marked “included by default”; each project then keeps its own copy.">
        <button className="btn btn-primary" onClick={() => setEdit(blank())}>
          <Plus className="icon" />
          Add agent
        </button>
      </PageHeader>
      {lib.isLoading ? (
        <Spinner />
      ) : (
        <section className="card audit-card">
          <table className="table">
            <thead>
              <tr>
                <th>Agent</th>
                <th style={{ width: 100 }}>Role</th>
                <th style={{ width: 150 }}>Preferred model</th>
                <th style={{ width: 120 }}>Tools on</th>
                <th style={{ width: 150 }}>New projects</th>
              </tr>
            </thead>
            <tbody>
              {(lib.data ?? []).map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer', opacity: t.enabled ? 1 : 0.55 }} onClick={() => setEdit(t)}>
                  <td>
                    <b>{t.name}</b>
                    {t.isDefaultAgent && <span className="tag tag-accent sm" style={{ marginLeft: 8 }}>Default agent</span>}
                    {!t.enabled && <span className="tag sm" style={{ marginLeft: 8 }}>Hidden</span>}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {t.description || '—'}
                    </div>
                  </td>
                  <td>{t.role}</td>
                  <td className={t.preferredModel ? 'mono' : 'faint'} style={{ fontSize: 12 }}>
                    {t.preferredModel ?? 'their choice'}
                  </td>
                  <td style={{ fontSize: 13 }}>{TOOL_DEFS.filter((d) => t.tools[d.key]?.on).map((d) => d.label.replace('File ', '')).join(', ') || '—'}</td>
                  <td style={{ fontSize: 13 }}>{t.includeByDefault ? 'Included by default' : <span className="muted">Optional</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {edit && <TemplateEditor initial={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function TemplateEditor({ initial, onClose }: { initial: Draft; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: models = [] } = useModels();
  const [d, setD] = useState<Draft>(initial);
  const [busy, setBusy] = useState(false);
  const up = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const setTool = (k: (typeof TOOL_KEYS)[number], patch: Partial<{ on: boolean; perm: Permission }>) => up({ tools: { ...d.tools, [k]: { ...d.tools[k], ...patch } } });
  // Like the Agents page: a role brings its tools, unless they were tuned by hand.
  const changeRole = (role: AgentRole) => {
    const untouched = sameTools(d.tools, ROLE_TOOLS[d.role]) || sameTools(d.tools, ROLE_TOOLS.Custom);
    up(untouched ? { role, tools: ROLE_TOOLS[role] } : { role });
  };
  const chatModels = [...new Set(models.filter((m) => m.capabilities.kind === 'chat').map((m) => m.modelName))];

  const save = async () => {
    setBusy(true);
    try {
      const { id, ...body } = d;
      if (id) await api.put(`/api/admin/agent-templates/${id}`, body);
      else await api.post('/api/admin/agent-templates', body);
      await qc.invalidateQueries({ queryKey: key });
      toast(id ? `${d.name} saved · applies to projects and agents added from now on` : `${d.name} added to the library`);
      onClose();
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!d.id || !confirm(`Remove ${d.name} from the library? Projects that already have it keep their copy.`)) return;
    try {
      await api.del(`/api/admin/agent-templates/${d.id}`);
      await qc.invalidateQueries({ queryKey: key });
      toast(`${d.name} removed from the library`);
      onClose();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <Modal open onClose={onClose} label="Library agent" width={820}>
      <div className="dialog-title">{d.id ? `Edit ${initial.name}` : 'New library agent'}</div>
      <div className="stack-sm" style={{ maxHeight: '70vh', overflow: 'auto', paddingRight: 4 }}>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="lt-name">Name</label>
            <input id="lt-name" className="input" value={d.name} onChange={(e) => up({ name: e.target.value })} placeholder="e.g. Laravel Coder" />
          </div>
          <div className="field">
            <span className="label">Role</span>
            <Seg<AgentRole> value={d.role} onChange={changeRole} options={ROLES.map((r) => ({ value: r, label: r }))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="lt-desc">Description (shown when people pick agents)</label>
          <input id="lt-desc" className="input" value={d.description} onChange={(e) => up({ description: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="lt-model">Preferred model</label>
          <input id="lt-model" className="input mono" list="lt-models" value={d.preferredModel ?? ''} onChange={(e) => up({ preferredModel: e.target.value.trim() || null })} placeholder="e.g. gpt-6-sol · empty = the model they pick" />
          <datalist id="lt-models">
            {chatModels.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <div className="hint">Used when the person has a model with this name among their keys; otherwise the model they choose for the project.</div>
        </div>
        <div className="field">
          <label htmlFor="lt-prompt">System prompt · variables: {'{{project.name}} {{server.cwd}} {{date}}'}</label>
          <textarea id="lt-prompt" className="input mono" style={{ minHeight: 160, lineHeight: 1.6 }} value={d.systemPrompt} onChange={(e) => up({ systemPrompt: e.target.value })} />
        </div>
        <div className="field">
          <span className="label">Tools</span>
          <table className="table">
            <tbody>
              {TOOL_DEFS.map((t) => (
                <tr key={t.key}>
                  <td style={{ width: 50 }}>
                    <Switch on={d.tools[t.key].on} onChange={(v) => setTool(t.key, { on: v })} label={t.label} />
                  </td>
                  <td>
                    <b>{t.label}</b>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {t.desc}
                    </div>
                  </td>
                  <td style={{ width: 160 }}>
                    <select className="input" value={d.tools[t.key].perm} disabled={!d.tools[t.key].on} onChange={(e) => setTool(t.key, { perm: e.target.value as Permission })} aria-label={`${t.label} permission`}>
                      {PERMS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="lt-calls">Tool calls per turn</label>
            <input
              id="lt-calls"
              className="input"
              inputMode="numeric"
              value={d.limits.maxToolCalls}
              onChange={(e) => up({ limits: { ...d.limits, maxToolCalls: Math.min(TOOL_CALLS_MAX, Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1)) } })}
            />
          </div>
          <div className="field">
            <label htmlFor="lt-files">Context files (comma separated)</label>
            <input id="lt-files" className="input mono" value={d.contextFiles.join(', ')} onChange={(e) => up({ contextFiles: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} />
          </div>
        </div>
        <SwitchRow on={d.includeByDefault} onChange={(v) => up({ includeByDefault: v })}>
          Included by default in new projects
        </SwitchRow>
        <SwitchRow on={d.isDefaultAgent} onChange={(v) => up({ isDefaultAgent: v })}>
          Default agent: answers new conversations when included
        </SwitchRow>
        <SwitchRow on={d.enabled} onChange={(v) => up({ enabled: v })}>
          Visible to people (turn off to hide it without deleting)
        </SwitchRow>
      </div>
      <div className="dialog-actions">
        {d.id && (
          <button className="btn btn-ghost" style={{ marginRight: 'auto' }} onClick={() => void remove()}>
            Remove from library
          </button>
        )}
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={busy || !d.name.trim() || !d.systemPrompt.trim()} onClick={() => void save()}>
          {d.id ? 'Save' : 'Add to library'}
        </button>
      </div>
    </Modal>
  );
}
